/**
 * The high-level "make sure the prover has its params" surface.
 *
 * Two-tier ensure flow:
 *
 *   IndexedDB?      ┐
 *   yes → return    │  fast path: ~50 ms IDB read
 *   no:             │
 *     Cache Storage?
 *     yes → decompress in worker → verify SHA-256 → write IDB → return
 *     no:
 *       fetch from CDN → cache → decompress → verify → write IDB → return
 *
 * Idempotent. Concurrent callers share the same in-flight promise so
 * we never double-fetch or double-decompress.
 */

import { SAPLING_PARAMS_BASE_URL } from "../config";
import {
  hasCompressedShard,
  prefetchCompressedShard,
  readCompressedShard,
} from "./cache";
import {
  decompressShardInWorker,
  disposeDecompressWorker,
} from "./decompress-worker-client";
import {
  CANONICAL_SHA256,
  PARAM_FILES,
  type ParamKind,
} from "./hashes";
import {
  getCanonicalBytes,
  hasCanonicalBytes,
  putCanonicalBytes,
} from "./storage";

// Canonical (uncompressed) param URLs. Trying these first means slow
// devices don't have to run the bls12-381 decompression — they trade
// ~25 MB more bandwidth for zero CPU work, which is the right call
// for mobile WebViews where decompressing 47 MB of points reliably
// took longer than the user was willing to wait.
const canonicalUrl = (kind: ParamKind): string =>
  `${SAPLING_PARAMS_BASE_URL.replace(/\/+$/, "")}/canonical/${PARAM_FILES[kind].compressed.replace(/\.cz$/, "")}`;

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const view = new Uint8Array(bytes.buffer as ArrayBuffer, bytes.byteOffset, bytes.byteLength);
  const buf = await crypto.subtle.digest("SHA-256", view);
  const arr = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < arr.length; i++) s += arr[i]!.toString(16).padStart(2, "0");
  return s;
}

/**
 * Streams the canonical .params file directly with progress reporting.
 * Returns null if the server doesn't expose canonical bytes (404), so
 * the caller can fall back to the compressed+decompress path.
 */
async function fetchCanonicalDirect(
  kind: ParamKind,
  onProgress?: (loaded: number, total: number | null) => void,
): Promise<Uint8Array | null> {
  const url = canonicalUrl(kind);
  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    return null;
  }
  if (res.status === 404) return null;
  if (!res.ok) return null;

  const total = Number(res.headers.get("content-length")) || PARAM_FILES[kind].canonicalSize;
  const reader = res.body?.getReader();
  if (!reader) {
    const buf = new Uint8Array(await res.arrayBuffer());
    onProgress?.(buf.byteLength, total);
    return buf;
  }
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      onProgress?.(loaded, total);
    }
  }
  const out = new Uint8Array(loaded);
  let off = 0;
  for (const c of chunks) {
    out.set(c, off);
    off += c.byteLength;
  }
  return out;
}

export type EnsureProgress =
  | { stage: "checking" }
  | { stage: "fetching"; kind: ParamKind; loaded: number; total: number | null }
  | { stage: "decompressing"; kind: ParamKind }
  | { stage: "verifying"; kind: ParamKind }
  | { stage: "writing"; kind: ParamKind }
  | { stage: "done" };

interface InFlight {
  promise: Promise<Uint8Array>;
}

const inflightByKind = new Map<ParamKind, InFlight>();

/**
 * Return canonical bytes for the requested kind. Triggers
 * prefetch + decompress + verify if needed. Multiple concurrent
 * callers for the same kind share the same work.
 */
export function ensureCanonicalParams(
  kind: ParamKind,
  onProgress?: (p: EnsureProgress) => void,
): Promise<Uint8Array> {
  const existing = inflightByKind.get(kind);
  if (existing) return existing.promise;

  const run = ensureCanonicalParamsImpl(kind, onProgress).finally(() => {
    inflightByKind.delete(kind);
  });
  inflightByKind.set(kind, { promise: run });
  return run;
}

async function ensureCanonicalParamsImpl(
  kind: ParamKind,
  onProgress?: (p: EnsureProgress) => void,
): Promise<Uint8Array> {
  onProgress?.({ stage: "checking" });

  // 1. IDB fast path
  if (await hasCanonicalBytes(kind)) {
    const cached = await getCanonicalBytes(kind);
    if (cached && cached.byteLength === PARAM_FILES[kind].canonicalSize) {
      onProgress?.({ stage: "done" });
      return cached;
    }
    // Bytes are present but the wrong size — IDB row is stale/corrupt.
    // Fall through to re-derive.
  }

  // 2. Canonical-direct path — preferred on mobile because there's no
  //    decompression CPU cost. We pay ~25 MB more bandwidth per shard
  //    in exchange for never running the bls12-381 batch decompress
  //    that took >180 s on at least one iOS WebView in the wild.
  const direct = await fetchCanonicalDirect(kind, (loaded, total) =>
    onProgress?.({ stage: "fetching", kind, loaded, total }),
  );
  if (direct && direct.byteLength === PARAM_FILES[kind].canonicalSize) {
    onProgress?.({ stage: "verifying", kind });
    const actual = await sha256Hex(direct);
    if (actual !== CANONICAL_SHA256[kind]) {
      throw new Error(
        `canonical ${kind} SHA-256 mismatch: expected ${CANONICAL_SHA256[kind]}, got ${actual}`,
      );
    }
    onProgress?.({ stage: "writing", kind });
    await putCanonicalBytes(kind, direct);
    onProgress?.({ stage: "done" });
    return direct;
  }

  // 3. Compressed-then-decompress fallback (older deployments without
  //    canonical files served). Slow on mobile but guaranteed to work
  //    wherever Cache Storage + the codec worker are available.
  if (!(await hasCompressedShard(kind))) {
    await prefetchCompressedShard(kind, {
      onProgress: (loaded, total) =>
        onProgress?.({ stage: "fetching", kind, loaded, total }),
    });
  }
  const compressed = await readCompressedShard(kind);

  onProgress?.({ stage: "decompressing", kind });
  const canonical = await decompressShardInWorker({
    compressed,
    expectedSha256: CANONICAL_SHA256[kind],
  });
  onProgress?.({ stage: "verifying", kind });

  onProgress?.({ stage: "writing", kind });
  await putCanonicalBytes(kind, canonical);
  onProgress?.({ stage: "done" });
  return canonical;
}

/**
 * Ensure both shards are ready. Useful as the post-mnemonic onboarding
 * step: by the time the user reaches Send, the prover has its params.
 */
export async function ensureAllSaplingParams(
  onProgress?: (p: EnsureProgress) => void,
): Promise<void> {
  await ensureCanonicalParams("saplingSpend", onProgress);
  await ensureCanonicalParams("saplingOutput", onProgress);
  // Worker holds ~50 MB of WASM heap; release once we're done.
  disposeDecompressWorker();
}
